/**
 * Module dependencies.
 */

import { Text, baseFontSize, clampCalc } from 'src/components/core/text';
import { TourData } from 'src/types/tour';
import { media } from 'src/styles/media';
import { normalizeNewLine } from 'src/core/utils/string';
import FadeInAnimation from 'src/components/core/animations/fade-in';
import FadeInRightAnimation from 'src/components/core/animations/fade-in-right';
import FadeInUpAnimation from 'src/components/core/animations/fade-in-up';
import Image from 'src/components/core/image';
import RawHtml from 'src/components/core/raw-html';
import Svg from 'src/components/core/svg';
import iconPlay from 'src/assets/svg/play.svg';
import styled from 'styled-components';
import useBreakpoint from 'src/hooks/use-breakpoint';
import useTranslate from 'src/hooks/use-translate';

/**
 * `Props` type.
 */

type Props = Partial<TourData['sections']['guest']>;

/**
 * `Wrapper` styled component.
 */

const Wrapper = styled.div`
  padding: 0 var(--gutter-mobile);

  ${media.min.md`
    display: grid;
    grid-template-areas:
      '. .            .            . image'
      '. titleWrapper titleWrapper . image'
      '. .            paragraph    . image'
      '. .            .            . image';
    grid-template-columns: 0.3fr 0.3fr 5.6fr 0.4fr 5fr;
    grid-template-rows: 8vw 8vw 1fr 6vw;
    height: 100%;
    padding: 0;
    width: 190vw;

    @media screen and (min-height: 700px) {
      width: 150vw;
    }

    @media screen and (min-height: 800px) {
      width: 130vw;
    }

    @media screen and (min-height: 900px) {
      width: 120vw;

    }
  `}

  ${media.min.lg`
    width: 170vw;
    grid-template-columns: 0.2fr 0.2fr 5.6fr 0.4fr 3.6fr;
    grid-template-rows: 7vw 8vw 1fr 6vw;

    @media screen and (min-height: 700px) {
      width: 125vw;
    }

    @media screen and (min-height: 800px) {
      width: 110vw;
    }

    @media screen and (min-height: 900px) {
      grid-template-columns: 0.5fr 1fr 5fr 1fr 5.5fr;
      grid-template-rows: 6vw max-content 1fr 6vw;
      width: 125vw;
    }

    @media screen and (min-height: 1000px) {
      width: 110vw;
    }
  `}

  ${media.min.xl`
    width: 140vw;
    grid-template-columns: 0.2fr 0.2fr 5.6fr 0.4fr 5.5fr;
    grid-template-rows: 5.9vw 6.5vw 1fr 6vw;

    @media screen and (min-height: 900px) {
      grid-template-columns: 0.5fr 0.5fr 5.4fr 0.6fr 5.2fr;
      grid-template-rows: 6vw 8vw 1fr 6vw;
      width: 120vw;
    }

    @media screen and (min-height: 1000px) {
      grid-template-columns: 0.5fr 1fr 5fr 1fr 5.5fr;
      grid-template-rows: 6vw max-content 1fr 6vw;
      width: 110vw;
    }
  `}

  ${media.min.xxl`
    width: 140vw;
    grid-template-columns: 0.3fr 0.3fr 5.6fr 0.4fr 5.5fr;
    grid-template-rows: 5.2vw 5.3vw 1fr 6vw;

    @media screen and (min-height: 900px) {
      grid-template-columns: 0.5fr 0.4fr 5.5fr 0.7fr 5.2fr;
      grid-template-rows: 4.6vw 5.7vw 1fr 6vw;
      width: 110vw;
    }

    @media screen and (min-height: 1000px) {
      grid-template-columns: 0.5fr 0.6fr 5.3fr 0.8fr 5.2fr;
      grid-template-rows: 6vw 6vw 1fr 6vw;
      width: 100vw;
    }
  `}
`;

/**
 * `TitleWrapper` styled component.
 */

const TitleWrapper = styled.div`
  display: flex;
  flex-direction: column;
  grid-area: titleWrapper;
  justify-content: center;
  margin-bottom: 4rem;

  ${media.min.md`
    margin-bottom: 8rem;
    z-index: 1;
  `}
`;

/**
 * `Title` styled component.
 */

const Title = styled(Text).attrs({
  as: 'h2',
  uppercase: true,
  variant: 'boldM'
})`
  color: var(--color-dark);
  margin: 0;
  position: relative;
`;

/**
 * `HandWrittenText` styled component.
 */

const HandWrittenText = styled(Text).attrs({ variant: 'handwrittenS' })`
  bottom: -80%;
  color: var(--color-yellow);
  left: 10%;
  margin: 0;
  position: absolute;
  text-transform: none;
  white-space: nowrap;
  z-index: 1;

  @supports (-webkit-hyphens: none) {
    font-size: clamp(30px, ${clampCalc(30, 43)}, 43px);
  }
`;

/**
 * `HandWrittenTextMobile` styled component.
 */

const HandWrittenTextMobile = styled(Text).attrs({ variant: 'handwrittenS' })`
  color: var(--color-yellow);
  display: block;
  margin: 0;
  text-align: center;

  @supports (-webkit-hyphens: none) {
    font-size: clamp(24px, ${clampCalc(24, 43)}, 43px);
  }
`;

/**
 * `Paragraph` styled component.
 */

const Paragraph = styled(Text).attrs({
  as: 'p',
  variant: 'paragraph'
})`
  color: var(--color-grey-700);
  font-size: clamp(${16 / baseFontSize}rem, ${clampCalc(16, 18)}, ${18 / baseFontSize}rem);
  line-height: clamp(${16 / baseFontSize}rem, ${clampCalc(16, 19)}, ${19 / baseFontSize}rem);

  ${media.max.md`
    margin-bottom: 3rem;
  `}

  @media (min-width: 1920px) {
    line-height: ${30 / baseFontSize}rem;
  }
`;

/**
 * `ContinueWrapper` styled component.
 */

const ContinueWrapper = styled.div`
  display: flex;
  flex-direction: row;
  gap: 0.625rem;
  justify-content: flex-start;
`;

/**
 * `TextWrapper` styled component.
 */

const TextWrapper = styled.div`
  width: 90%;

  ${media.min.xl`
    width: 50%
  `};
`;

/**
 * `ButtonTitle` styled component.
 */

const ButtonTitle = styled(Text).attrs({
  as: 'h3',
  uppercase: true,
  variant: 'boldM'
})`
  color: var(--color-dark);
  font-size: 1.25rem;
  line-height: 1.875rem;
  margin: 0;
  position: relative;
`;

/**
 * `ContinueText` styled component.
 */

const ContinueText = styled(Text).attrs({
  as: 'p',
  variant: 'paragraph'
})`
  color: var(--color-dark);
  font-size: 0.8125rem;
`;

/**
 * `CoverImageWrapper` styled component.
 */

const CoverImageWrapper = styled.div`
  height: 100%;
  overflow: hidden;
  position: relative;

  ${media.max.md`
   margin-bottom: 4rem;
   padding-bottom: 135%;
 `};
`;

/**
 * `Biography` component.
 */

const Biography = (props: Props) => {
  const { assets, author, biography, continueText } = props;

  const { translate } = useTranslate();
  const isMobile = useBreakpoint('md', 'max');

  return (
    <Wrapper>
      <TitleWrapper>
        {!isMobile && (
          <FadeInRightAnimation options={{ transitionTime: 1 }}>
            <Title>
              {translate('common:labels.meet')}

              <HandWrittenText>{author?.fullName}</HandWrittenText>
            </Title>
          </FadeInRightAnimation>
        )}

        {isMobile && (
          <>
            <FadeInUpAnimation options={{ transitionTime: 1 }}>
              <Title>{translate('common:labels.meet')}</Title>
            </FadeInUpAnimation>

            <FadeInUpAnimation
              options={{
                distance: '100%',
                transitionTime: 1
              }}
            >
              <HandWrittenTextMobile>{author?.fullName}</HandWrittenTextMobile>
            </FadeInUpAnimation>
          </>
        )}
      </TitleWrapper>

      <div style={{ gridArea: 'paragraph' }}>
        {!isMobile && (
          <FadeInRightAnimation
            options={{
              threshold: 0.5,
              transitionTime: 1
            }}
          >
            <RawHtml element={Paragraph}>{normalizeNewLine(biography)}</RawHtml>
          </FadeInRightAnimation>
        )}

        {!isMobile && (
          <FadeInRightAnimation
            options={{
              threshold: 0.5,
              transitionTime: 1
            }}
          >
            <ContinueWrapper>
              <Svg icon={iconPlay} size={'1.5rem'} />

              <TextWrapper>
                <ButtonTitle>{translate('common:sections.tour.keep')}</ButtonTitle>

                <RawHtml element={ContinueText}>{continueText}</RawHtml>
              </TextWrapper>
            </ContinueWrapper>
          </FadeInRightAnimation>
        )}

        {isMobile && (
          <FadeInUpAnimation options={{ transitionTime: 1 }}>
            <RawHtml element={Paragraph}>{normalizeNewLine(biography)}</RawHtml>
          </FadeInUpAnimation>
        )}
      </div>

      {isMobile && (
        <FadeInUpAnimation
          options={{
            threshold: 0.5,
            transitionTime: 1
          }}
        >
          <ContinueWrapper>
            <Svg icon={iconPlay} size={'1.5rem'} />

            <TextWrapper>
              <ButtonTitle>{translate('common:sections.tour.keep')}</ButtonTitle>

              <RawHtml element={ContinueText}>{continueText}</RawHtml>
            </TextWrapper>
          </ContinueWrapper>
        </FadeInUpAnimation>
      )}

      <div style={{ gridArea: 'image' }}>
        <FadeInAnimation fullHeight options={{ transitionTime: 1 }}>
          {assets?.bioImage && (
            <CoverImageWrapper>
              <Image
                alt={author?.fullName}
                layout={'fill'}
                objectFit={'cover'}
                objectPosition={'top'}
                src={assets?.bioImage}
              />
            </CoverImageWrapper>
          )}
        </FadeInAnimation>
      </div>
    </Wrapper>
  );
};

/**
 * Export `Biography` component.
 */

export default Biography;
